<template>
  <div class="training-event-list-view pa-4">
    <!-- Header Neutro -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-icon color="primary" large class="mr-3">mdi-calendar-multiselect</v-icon>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Eventos de Capacitación
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Convocatorias, logística, difusión en Ari Sunshine y control centralizado de cursos.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- Filtros de Búsqueda -->
    <v-card class="mb-5 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <s-select-definition :def="1067" label="Tipo de Instrucción" v-model="filters.TypeInstruction" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-select-definition :def="1459" label="Modalidad" v-model="filters.TypeModality" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-select-campaign-se label="Campaña" v-model="filters.CmpID" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date label="Fecha Desde" v-model="filters.DateInitial" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date label="Fecha Hasta" v-model="filters.DateFin" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="1">
            <v-btn block color="primary" class="rounded-lg font-weight-bold white--text text-none py-4" elevation="0" :loading="loading" @click="fetchEvents">
              <v-icon small>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Grilla de Datos Principal -->
    <v-card class="rounded-xl border-light bg-white pa-4 mb-5" outlined elevation="0">
      <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="grey darken-3" class="mr-2">mdi-calendar-check</v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
            Eventos Registrados ({{ events.length }})
          </span>
        </div>
        <div class="d-flex align-center">
          <v-btn
            color="primary"
            outlined
            class="rounded-lg text-none font-weight-bold px-3 mr-2"
            small
            @click="$router.push('/management/training/dashboard')"
          >
            <v-icon left small>mdi-view-dashboard-outline</v-icon>
            Dashboard de Cobertura
          </v-btn>
          <v-btn color="primary" class="rounded-lg text-none font-weight-bold px-4" small elevation="0" @click="openCreateModal">
            <v-icon left small>mdi-plus</v-icon>
            Nuevo Evento
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="events"
        :loading="loading"
        dense
        class="border-rounded elevation-0"
        :items-per-page="15"
      >
        <template v-slot:[`item.TevName`]="{ item }">
          <div class="font-weight-bold grey--text text--darken-4">
            {{ item.TevName }}
          </div>
          <div
            class="text-caption grey--text text--darken-1 text-truncate"
            style="max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
            v-if="item.TevDescription"
            :title="item.TevDescription"
          >
            {{ item.TevDescription }}
          </div>
        </template>

        <template v-slot:[`item.TevStartDateTime`]="{ item }">
          <span>{{ formatDateTime(item.TevStartDateTime) }}</span>
        </template>

        <template v-slot:[`item.TypeModalityName`]="{ item }">
          <v-chip small :color="getModalityColor(item.TypeModality)" dark class="font-weight-bold">
            {{ item.TypeModalityName || 'Presencial' }}
          </v-chip>
        </template>

        <template v-slot:[`item.TevIsVisibleApp`]="{ item }">
          <v-chip x-small :color="item.TevIsVisibleApp === 1 ? 'success' : 'grey'" dark>
            <v-icon left x-small>{{ item.TevIsVisibleApp === 1 ? 'mdi-cellphone-check' : 'mdi-cellphone-off' }}</v-icon>
            {{ item.TevIsVisibleApp === 1 ? 'Visible App' : 'Oculto' }}
          </v-chip>
        </template>

        <template v-slot:[`item.TotalSessions`]="{ item }">
          <v-btn text x-small color="primary" class="font-weight-bold text-none" @click="goToSessions(item)">
            <v-icon left small>mdi-clock-outline</v-icon>
            {{ item.TotalSessions }} Sesiones
          </v-btn>
        </template>

        <template v-slot:[`item.TotalParticipants`]="{ item }">
          <div class="d-flex flex-column align-center py-1">
            <v-btn text x-small color="primary" class="font-weight-bold text-none pa-0" @click="goToParticipants(item)">
              <v-icon left small>mdi-account-group-outline</v-icon>
              {{ item.TotalParticipants }} Convocados
            </v-btn>
            <div class="d-flex align-center mt-1" v-if="item.TotalParticipants > 0">
              <v-chip x-small color="success" class="mr-1 font-weight-bold" dark :title="`${item.TotalConfirmed || 0} confirmaron asistencia`">
                <v-icon left x-small>mdi-check</v-icon>{{ item.TotalConfirmed || 0 }}
              </v-chip>
              <v-chip x-small color="error" class="font-weight-bold" dark v-if="item.TotalDeclined > 0" :title="`${item.TotalDeclined} declinaron`">
                <v-icon left x-small>mdi-close</v-icon>{{ item.TotalDeclined }}
              </v-chip>
            </div>
          </div>
        </template>

        <template v-slot:[`item.TevStatusName`]="{ item }">
          <v-chip small :color="getStatusColor(item.TevStatus)" dark class="font-weight-bold">
            {{ item.TevStatusName }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom v-if="item.TevFlyerPath">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="indigo" v-bind="attrs" v-on="on" @click="viewFlyer(item)">
                <v-icon small>mdi-image</v-icon>
              </v-btn>
            </template>
            <span>Ver Flyer Móvil</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openEditModal(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Editar Evento</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="deactivateEvent(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>Desactivar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Formulario de Evento -->
    <TrainingEventFormModal
      v-model="modal.show"
      :edit-item="modal.item"
      @saved="fetchEvents"
    />

    <!-- Modal Visor de Flyer -->
    <TrainingEventFlyerModal
      v-model="flyerModal.show"
      :event-item="flyerModal.item"
      @flyerSelected="onFlyerUpdated"
    />
  </div>
</template>

<script>
import SDate from "@/components/Utils/SDate.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SSelectCampaignSe from "@/components/Utils/SSelectCampaignSe.vue";
import TrainingEventService from "@/services/Management/TrainingEventService";
import TrainingEventFormModal from "./TrainingEventFormModal.vue";
import TrainingEventFlyerModal from "./TrainingEventFlyerModal.vue";

export default {
  name: "TrainingEventList",
  components: {
    SDate,
    SSelectDefinition,
    SSelectCampaignSe,
    TrainingEventFormModal,
    TrainingEventFlyerModal,
  },
  data() {
    return {
      loading: false,
      filters: {
        TypeInstruction: null,
        TypeModality: null,
        TevStatus: null,
        DateInitial: null,
        DateFin: null,
        CmpID: null,
        Search: "",
        SecStatus: 1,
      },
      events: [],
      headers: [
        { text: "ID", value: "TevID", align: "center", width: "60px" },
        { text: "Evento", value: "TevName", align: "start" },
        { text: "Inicio", value: "TevStartDateTime", width: "140px" },
        { text: "Modalidad", value: "TypeModalityName", align: "center", width: "110px" },
        { text: "App Móvil", value: "TevIsVisibleApp", align: "center", width: "110px" },
        { text: "Sesiones", value: "TotalSessions", align: "center", width: "120px" },
        { text: "Participantes", value: "TotalParticipants", align: "center", width: "130px" },
        { text: "Estado", value: "TevStatusName", align: "center", width: "120px" },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: "120px" },
      ],
      modal: {
        show: false,
        item: null,
      },
      flyerModal: {
        show: false,
        item: null,
      },
    };
  },
  created() {
    this.fetchEvents();
  },
  methods: {
    fetchEvents() {
      this.loading = true;
      const reqId = this.$fun.getUserID();
      TrainingEventService.list(this.filters, reqId)
        .then((res) => {
          this.events = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.$fun.alert("Error al consultar eventos de capacitación.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDateTime(dt) {
      if (!dt) return "-";
      return dt.replace("T", " ").substring(0, 16);
    },
    getModalityColor(mod) {
      if (mod === 1) return "primary";
      if (mod === 2) return "teal darken-1";
      if (mod === 3) return "indigo darken-1";
      return "grey";
    },
    getStatusColor(st) {
      if (st === 1) return "info";
      if (st === 2) return "warning";
      if (st === 3) return "success";
      if (st === 4) return "error";
      return "grey";
    },
    openCreateModal() {
      this.modal.item = null;
      this.modal.show = true;
    },
    openEditModal(item) {
      this.modal.item = Object.assign({}, item);
      this.modal.show = true;
    },
    viewFlyer(item) {
      this.flyerModal.item = Object.assign({}, item);
      this.flyerModal.show = true;
    },
    onFlyerUpdated(flyerPath) {
      if (this.flyerModal.item) {
        const reqId = this.$fun.getUserID();
        const payload = Object.assign({}, this.flyerModal.item, { TevFlyerPath: flyerPath });
        TrainingEventService.save(payload, reqId).then(() => {
          this.$fun.alert("Flyer actualizado correctamente.", "success");
          this.fetchEvents();
        });
      }
    },
    goToSessions(item) {
      this.$router.push({
        path: "/management/training/sessions",
        query: { tevID: item.TevID, eventName: item.TevName },
      });
    },
    goToParticipants(item) {
      this.$router.push({
        path: "/management/training/participants",
        query: { tevID: item.TevID, eventName: item.TevName },
      });
    },
    deactivateEvent(item) {
      this.$fun.alert("¿Está seguro de desactivar este evento?", "question").then((r) => {
        if (r && r.value) {
          const reqId = this.$fun.getUserID();
          const payload = Object.assign({}, item, { SecStatus: 0 });
          TrainingEventService.save(payload, reqId)
            .then(() => {
              this.$fun.alert("Evento desactivado correctamente.", "success");
              this.fetchEvents();
            })
            .catch(() => {
              this.$fun.alert("Error al desactivar el evento.", "error");
            });
        }
      });
    },
  },
};
</script>
